import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout,
  Card,
  Button,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  DataTable,
  Badge,
  Modal,
  Tabs,
  Spinner,
  EmptyState,
} from '@shopify/polaris';
import { apiGet, apiPut, apiDelete, apiPost } from '../api';
import { DEFAULT_TRANSLATION_KEYS } from './defaultTranslationKeys';

const LOCALES = [
  { label: 'Español', value: 'es' },
  { label: 'English', value: 'en' },
  { label: 'Français', value: 'fr' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Italiano', value: 'it' },
  { label: 'Português', value: 'pt' },
  { label: 'Nederlands', value: 'nl' },
  { label: 'Polski', value: 'pl' },
];

interface Translation {
  id: number;
  key: string;
  locale: string;
  value: string;
  updatedAt: string;
}

export default function AppTranslations() {
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLocale, setSelectedLocale] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importLocale, setImportLocale] = useState('es');

  const locale = LOCALES[selectedLocale].value;

  const fetchTranslations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet<{ translations: Translation[] }>('/panel-api/translations');
      setTranslations(res.translations);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  // All default keys (from es locale as reference)
  const allDefaultKeys = useMemo(() => Object.keys(DEFAULT_TRANSLATION_KEYS['es'] ?? {}), []);

  // Group translations by key, merging with defaults from all locales
  const translationsByKey = useMemo(() => {
    const map = new Map<string, Map<string, Translation>>();

    // Seed all default keys with their translations per locale
    for (const key of allDefaultKeys) {
      const localeMap = new Map<string, Translation>();
      for (const [loc, translations] of Object.entries(DEFAULT_TRANSLATION_KEYS)) {
        if (translations[key]) {
          localeMap.set(loc, {
            id: 0,
            key,
            locale: loc,
            value: translations[key],
            updatedAt: '',
          });
        }
      }
      map.set(key, localeMap);
    }

    // Override with actual DB translations
    for (const t of translations) {
      if (!map.has(t.key)) map.set(t.key, new Map());
      map.get(t.key)!.set(t.locale, t);
    }
    return map;
  }, [translations, allDefaultKeys]);

  // Get all unique keys
  const allKeys = useMemo(() => {
    const keys = Array.from(translationsByKey.keys()).sort();
    if (!searchQuery) return keys;
    const q = searchQuery.toLowerCase();
    return keys.filter((k) => {
      if (k.toLowerCase().includes(q)) return true;
      // Also search values
      const localeMap = translationsByKey.get(k);
      if (localeMap) {
        for (const t of localeMap.values()) {
          if (t.value.toLowerCase().includes(q)) return true;
        }
      }
      return false;
    });
  }, [translationsByKey, searchQuery]);

  // Filter keys by selected locale tab
  const filteredKeys = useMemo(() => {
    return allKeys;
  }, [allKeys]);

  const startEditing = (key: string) => {
    const localeMap = translationsByKey.get(key);
    const values: Record<string, string> = {};
    for (const loc of LOCALES) {
      const t = localeMap?.get(loc.value);
      // DB-saved translations take priority, then defaults from locale files
      if (t && t.id > 0) {
        values[loc.value] = t.value;
      } else {
        values[loc.value] = DEFAULT_TRANSLATION_KEYS[loc.value]?.[key] ?? '';
      }
    }
    setEditValues(values);
    setEditingKey(key);
  };

  const saveEditing = async () => {
    if (!editingKey) return;
    setSaving(true);
    setError(null);
    try {
      const items = LOCALES.filter((loc) => editValues[loc.value]?.trim())
        .map((loc) => ({
          key: editingKey,
          locale: loc.value,
          value: editValues[loc.value].trim(),
        }));
      await apiPut('/panel-api/translations', { translations: items });
      setSuccess(`"${editingKey}" guardado correctamente`);
      setEditingKey(null);
      await fetchTranslations();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddKey = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const items = LOCALES.filter((loc) => newValues[loc.value]?.trim())
        .map((loc) => ({
          key: newKey.trim(),
          locale: loc.value,
          value: newValues[loc.value].trim(),
        }));
      if (items.length === 0) {
        setError('Añade al menos una traducción');
        setSaving(false);
        return;
      }
      await apiPut('/panel-api/translations', { translations: items });
      setSuccess(`"${newKey}" añadido correctamente`);
      setShowAddModal(false);
      setNewKey('');
      setNewValues({});
      await fetchTranslations();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (key: string) => {
    if (!confirm(`¿Eliminar "${key}" en todos los idiomas?`)) return;
    try {
      const localeMap = translationsByKey.get(key);
      if (localeMap) {
        for (const t of localeMap.values()) {
          await apiDelete(`/api/translations/${t.id}`);
        }
      }
      setSuccess(`"${key}" eliminado`);
      await fetchTranslations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleImport = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = JSON.parse(importJson);
      // Flatten nested JSON to dot-notation
      const flat: Record<string, string> = {};
      const flatten = (obj: any, prefix = '') => {
        for (const [k, v] of Object.entries(obj)) {
          const key = prefix ? `${prefix}.${k}` : k;
          if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
            flatten(v, key);
          } else {
            flat[key] = String(v);
          }
        }
      };
      flatten(data);
      
      const res = await apiPost<{ upserted: number }>('/panel-api/translations/import', {
        locale: importLocale,
        data: flat,
      });
      setSuccess(`Importadas ${res.upserted} traducciones para ${importLocale}`);
      setShowImportModal(false);
      setImportJson('');
      await fetchTranslations();
    } catch (err: any) {
      setError(err.message || 'JSON inválido');
    } finally {
      setSaving(false);
    }
  };

  const totalKeys = allDefaultKeys.length + 
    translations.filter((t) => !(DEFAULT_TRANSLATION_KEYS['es']?.[t.key])).length;

  const tabs = LOCALES.map((loc) => {
    const dbCount = translations.filter((t) => t.locale === loc.value).length;
    const defaultCount = Object.keys(DEFAULT_TRANSLATION_KEYS[loc.value] ?? {}).length;
    const hasDefaults = defaultCount > 0;
    return {
      id: loc.value,
      content: hasDefaults
        ? `${loc.label} (${dbCount > 0 ? dbCount + ' custom' : '✓ default'})`
        : `${loc.label} (${dbCount})`,
    };
  });

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          {success && (
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingLg" as="h2">
                  Traducciones de la App
                </Text>
                <InlineStack gap="200">
                  <Button onClick={() => setShowImportModal(true)}>
                    Importar JSON
                  </Button>
                  <Button variant="primary" onClick={() => { setShowAddModal(true); setNewKey(''); setNewValues({}); }}>
                    + Añadir clave
                  </Button>
                </InlineStack>
              </InlineStack>

              <TextField
                label=""
                placeholder="Buscar por clave o valor..."
                value={searchQuery}
                onChange={setSearchQuery}
                clearButton
                onClearButtonClick={() => setSearchQuery('')}
                autoComplete="off"
              />

              <Tabs tabs={tabs} selected={selectedLocale} onSelect={setSelectedLocale}>
                {loading ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner size="large" />
                  </div>
                ) : filteredKeys.length === 0 ? (
                  <EmptyState
                    heading="No hay traducciones"
                    image=""
                  >
                    <p>Añade claves de traducción o importa desde un archivo JSON.</p>
                  </EmptyState>
                ) : (
                  <div style={{ marginTop: '16px' }}>
                    {filteredKeys.map((key) => {
                      const localeMap = translationsByKey.get(key)!;
                      const currentValue = localeMap.get(locale)?.value;
                      const esValue = localeMap.get('es')?.value;
                      const isEditing = editingKey === key;
                      // Count DB-saved translations (id > 0) vs defaults
                      const dbSavedCount = LOCALES.filter((l) => {
                        const t = localeMap.get(l.value);
                        return t && t.id > 0;
                      }).length;
                      const defaultCount = LOCALES.filter((l) => {
                        const t = localeMap.get(l.value);
                        return t && t.id === 0;
                      }).length;
                      const localesWithValue = dbSavedCount;
                      const isDefault = dbSavedCount === 0;

                      return (
                        <div
                          key={key}
                          style={{
                            padding: '12px 0',
                            borderBottom: '1px solid #e1e3e5',
                          }}
                        >
                          {isEditing ? (
                            <BlockStack gap="300">
                              <InlineStack align="space-between" blockAlign="center">
                                <Text variant="bodyMd" as="span" fontWeight="semibold">
                                  {key}
                                </Text>
                                <InlineStack gap="200">
                                  <Button size="slim" onClick={() => setEditingKey(null)}>
                                    Cancelar
                                  </Button>
                                  <Button size="slim" variant="primary" onClick={saveEditing} loading={saving}>
                                    Guardar
                                  </Button>
                                </InlineStack>
                              </InlineStack>
                              {LOCALES.map((loc) => (
                                <TextField
                                  key={loc.value}
                                  label={loc.label}
                                  value={editValues[loc.value] ?? ''}
                                  onChange={(v) =>
                                    setEditValues((prev) => ({ ...prev, [loc.value]: v }))
                                  }
                                  autoComplete="off"
                                  multiline={editValues[loc.value]?.length > 80 ? 2 : undefined}
                                />
                              ))}
                            </BlockStack>
                          ) : (
                            <InlineStack align="space-between" blockAlign="center" wrap={false}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <InlineStack gap="200" blockAlign="center">
                                  <Text variant="bodyMd" as="span" fontWeight="semibold">
                                    {key}
                                  </Text>
                                  <Badge tone={isDefault && defaultCount >= 8 ? 'success' : localesWithValue >= 8 ? 'success' : localesWithValue >= 4 ? 'warning' : isDefault ? undefined : 'critical'}>
                                    {isDefault ? `✓ ${defaultCount}/8` : `${localesWithValue}/8 custom`}
                                  </Badge>
                                </InlineStack>
                                <div style={{ marginTop: '4px' }}>
                                  <Text variant="bodySm" as="span" tone="subdued">
                                    {localeMap.get(locale)?.id
                                      ? currentValue
                                      : currentValue
                                        ? `📋 ${currentValue}`
                                        : (esValue ? `[es] ${esValue}` : '—')}
                                  </Text>
                                </div>
                              </div>
                              <InlineStack gap="100">
                                <Button size="slim" onClick={() => startEditing(key)}>
                                  Editar
                                </Button>
                                <Button size="slim" tone="critical" onClick={() => handleDeleteKey(key)}>
                                  ✕
                                </Button>
                              </InlineStack>
                            </InlineStack>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Tabs>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>

      {/* Add Key Modal */}
      {showAddModal && (
        <Modal
          open
          onClose={() => setShowAddModal(false)}
          title="Añadir nueva clave"
          primaryAction={{ content: 'Añadir', onAction: handleAddKey, loading: saving }}
          secondaryActions={[{ content: 'Cancelar', onAction: () => setShowAddModal(false) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <TextField
                label="Clave"
                placeholder="home.newFeatureTitle"
                value={newKey}
                onChange={setNewKey}
                autoComplete="off"
                helpText="Usa notación con punto: sección.clave"
              />
              {LOCALES.map((loc) => (
                <TextField
                  key={loc.value}
                  label={loc.label}
                  value={newValues[loc.value] ?? ''}
                  onChange={(v) => setNewValues((prev) => ({ ...prev, [loc.value]: v }))}
                  autoComplete="off"
                />
              ))}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <Modal
          open
          onClose={() => setShowImportModal(false)}
          title="Importar traducciones desde JSON"
          primaryAction={{ content: 'Importar', onAction: handleImport, loading: saving }}
          secondaryActions={[{ content: 'Cancelar', onAction: () => setShowImportModal(false) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Select
                label="Idioma"
                options={LOCALES}
                value={importLocale}
                onChange={setImportLocale}
              />
              <TextField
                label="JSON"
                value={importJson}
                onChange={setImportJson}
                multiline={8}
                autoComplete="off"
                helpText="Pega el contenido de un archivo de traducciones (soporta JSON anidado)"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </Layout>
  );
}
